/**
 * One course in the catalog editor (plan docs/plans/lms-plan.md 8.1, 5.2,
 * 6.6, Appendix A, 18.17; WP-44; R10, R11), at
 * /lms/manage/catalog/courses/:courseId: the course's details (title, slug
 * until the first publish, description, validity, renewal lead days,
 * active), every version with its status and policy, a new draft (from the
 * current version or empty; one draft at a time, 5.2), the diff between any
 * two versions, and Retire (8.1: the current version retired, open
 * assignments cancelled, profile requirements removed; holders keep their
 * completions).
 *
 * Reads GET /courses/:id (an lms admin reads any course of the network) and
 * GET /admin/courses/:id/versions.
 *
 * "Add content" (course builder plan 8.1) opens the builder's wizard in
 * extend mode; it is rendered only when GET /health says the builder is on
 * for the network (G20), and is enabled only for a course with a published
 * current version and no open draft (the title says why otherwise).
 *
 * "Edit with AI" (the course editor, docs/plans/lms-course-editor-plan.md 8.1,
 * R1, D15, 18.2 Q15; WP-E31) is rendered only when GET /health says
 * builder.enabled, network_allowed AND builder.editor.enabled, for a course
 * with a published current version or an open draft that is neither an SOP
 * nor a read-and-acknowledge course (the edit routes refuse both, 6.2). With
 * an open draft it opens that draft's editor with the panel (?edit=1); with
 * none it confirms, makes the draft from the current version through the
 * existing create-draft route (no server-side draft creation, D15) and opens
 * it the same way.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Eye, FilePlus2, FileUp, GitCompare, ImageDown, Loader2, Pencil, Save, Sparkles } from 'lucide-react';
import ConfirmModal from '../../../../ui/ConfirmModal';
import Modal from '../../../../ui/Modal';
import { useToast } from '../../../../ui/Toast';
import { dangerBtnCls, outlineBtnCls, primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { apiErrorMessage } from '../../../api/client';
import { getCourse } from '../../../api/courses';
import type { CourseDetail } from '../../../api/courses';
import { createDraft, listCourseVersions, retireCourse, updateCourse } from '../../../api/catalog';
import type { CoursePatch, VersionSummary } from '../../../api/catalog';
import { listImagePacks } from '../../../api/imagePacks';
import type { ImagePackResult, ImagePackSummary } from '../../../api/imagePacks';
import { ImagePackModal } from './ImagePackModal';
import { noticeCls, pillCls } from '../../../components/player/styles';
import { builderPaths } from './builder/runModel';
import { useBuilderAvailability } from './builder/useBuildRun';
import { Checkbox, NumberInput, SelectField, TextArea, TextInput } from './fields';
import { CATEGORY_LABELS, catalogPaths, formatDate, KIND_LABELS, POLICY_TEXT, STATUS_LABELS, STATUS_TONES } from './model';
import { cardCls, hintCls, linkCls, rowCls, subheadingCls, tableWrapCls, tdCls, thCls } from './styles';

interface DetailsForm {
  title: string;
  slug: string;
  description: string;
  validity_months: number | null;
  renewal_lead_days: number | null;
  is_active: boolean;
}

function formOf(course: CourseDetail): DetailsForm {
  return {
    title: course.title,
    slug: course.slug,
    description: course.description ?? '',
    validity_months: course.validity_months ?? null,
    renewal_lead_days: course.renewal_lead_days ?? null,
    is_active: course.is_active !== false,
  };
}

/** Only the fields that changed travel (PATCH refuses an empty body). */
function patchOf(course: CourseDetail, form: DetailsForm, slugEditable: boolean): CoursePatch {
  const patch: CoursePatch = {};
  if (form.title.trim() !== course.title) patch.title = form.title.trim();
  if (slugEditable && form.slug.trim() !== course.slug) patch.slug = form.slug.trim();
  const description = form.description.trim() ? form.description.trim() : null;
  if (description !== (course.description ?? null)) patch.description = description;
  if (form.validity_months !== (course.validity_months ?? null)) patch.validity_months = form.validity_months;
  if (form.renewal_lead_days !== null && form.renewal_lead_days !== course.renewal_lead_days) patch.renewal_lead_days = form.renewal_lead_days;
  if (form.is_active !== (course.is_active !== false)) patch.is_active = form.is_active;
  return patch;
}

export function CoursePage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [course, setCourse] = useState<CourseDetail | null>(null);
  const [versions, setVersions] = useState<VersionSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<DetailsForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [retireOpen, setRetireOpen] = useState(false);
  const [retireReason, setRetireReason] = useState('');
  const [retiring, setRetiring] = useState(false);
  const [compareA, setCompareA] = useState('');
  const [compareB, setCompareB] = useState('');
  const [aiConfirmOpen, setAiConfirmOpen] = useState(false);
  // WP-75 (18.34): the image packs shipped for this course's slug; the button shows only when there is one.
  const [packs, setPacks] = useState<ImagePackSummary[]>([]);
  const [packOpen, setPackOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  // Builder plan 8.1, G20: "Add content" only when GET /health says the builder is on for the network.
  const builder = useBuilderAvailability();
  // Editor plan 8.1, G25 (18.2 Q15): "Edit with AI" only when health's editor block is on as well.
  const editorOn = builder.available && builder.health?.editor?.enabled === true;

  useEffect(() => {
    let cancelled = false;
    setError(null);
    if (!courseId) {
      setError('No course was named.');
      return undefined;
    }
    Promise.all([getCourse(courseId), listCourseVersions(courseId)])
      .then(([c, v]) => {
        if (cancelled) return;
        setCourse(c);
        setForm(formOf(c));
        setVersions(v);
        const newest = v[0]?.id ?? '';
        const previous = v[1]?.id ?? '';
        setCompareA(newest);
        setCompareB(previous);
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorMessage(err, 'The course could not be loaded.'));
      });
    return () => {
      cancelled = true;
    };
  }, [courseId, tick]);

  // The packs read is best effort: a failure (or an older api) hides the button and nothing else changes.
  useEffect(() => {
    let cancelled = false;
    if (!courseId) return undefined;
    listImagePacks(courseId)
      .then((items) => {
        if (!cancelled) setPacks(items);
      })
      .catch(() => {
        if (!cancelled) setPacks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  if (error) {
    return (
      <div className="space-y-3" data-testid="lms-catalog-course-error">
        <div className={noticeCls('red')} role="alert">
          {error}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={outlineBtnCls()} onClick={reload}>
            Retry
          </button>
          <Link to={catalogPaths.list()} className={outlineBtnCls()}>
            Back to the catalog
          </Link>
        </div>
      </div>
    );
  }
  if (!course || !form) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400" role="status">
        <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
        Loading the course...
      </div>
    );
  }

  const draft = versions.find((v) => v.status === 'draft') ?? null;
  const everPublished = versions.some((v) => v.published_at);
  const slugEditable = !everPublished; // 8.1: the slug is editable until the first publish
  const patch = patchOf(course, form, slugEditable);
  const dirty = Object.keys(patch).length > 0;
  const retired = !course.is_active && everPublished && !course.current_version;

  const saveDetails = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await updateCourse(course.id, patch);
      toast('Course details saved.');
      reload();
    } catch (err) {
      toast(apiErrorMessage(err, 'The course details could not be saved.'), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const newDraft = async (from: 'current' | 'empty') => {
    setCreating(true);
    try {
      const detail = await createDraft(course.id, from);
      toast(`Draft version ${detail.version.version_no} created.`);
      navigate(catalogPaths.version(detail.version.id));
    } catch (err) {
      const existing = (err as { response?: { data?: { draft_version_id?: string } } })?.response?.data?.draft_version_id;
      if (existing) {
        toast('This course already has a draft: opening it.', { type: 'info' });
        navigate(catalogPaths.version(existing));
        return;
      }
      toast(apiErrorMessage(err, 'The draft could not be created.'), { type: 'error' });
    } finally {
      setCreating(false);
    }
  };

  /** Editor plan 8.1, D15: the draft for Edit with AI is made from the current version by the existing route, then opened with the panel. */
  const withPanel = (versionId: string) => `${catalogPaths.version(versionId)}?edit=1`;
  const aiAllowed = course.category !== 'sop' && course.kind !== 'read_ack';
  const startAiDraft = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const detail = await createDraft(course.id, 'current');
      setAiConfirmOpen(false);
      toast(`Draft version ${detail.version.version_no} created. Tell the AI what to change in the panel.`);
      navigate(withPanel(detail.version.id));
    } catch (err) {
      setAiConfirmOpen(false);
      const existing = (err as { response?: { data?: { draft_version_id?: string } } })?.response?.data?.draft_version_id;
      if (existing) {
        toast('This course already has a draft: opening it.', { type: 'info' });
        navigate(withPanel(existing));
        return;
      }
      toast(apiErrorMessage(err, 'The draft could not be created.'), { type: 'error' });
    } finally {
      setCreating(false);
    }
  };

  /** WP-75: the server placed the pack on the draft; refresh the versions so "Open draft" appears, and say so. */
  const packApplied = (result: ImagePackResult) => {
    const n = result.placed.length;
    toast(n === 0 ? 'All of those images were already on the draft.' : `${n} image${n === 1 ? '' : 's'} placed on draft version ${result.version.version.version_no}. Review it and publish.`);
    reload();
  };

  const retire = async () => {
    if (!retireReason.trim() || retiring) return;
    setRetiring(true);
    try {
      await retireCourse(course.id, retireReason.trim());
      toast(`${course.title} retired. Holders keep their completions.`);
      setRetireOpen(false);
      setRetireReason('');
      reload();
    } catch (err) {
      toast(apiErrorMessage(err, 'The course could not be retired.'), { type: 'error' });
    } finally {
      setRetiring(false);
    }
  };

  const versionOptions = versions.map((v) => ({ value: v.id, label: `Version ${v.version_no} (${STATUS_LABELS[v.status].toLowerCase()})` }));

  return (
    <div className="space-y-4" data-testid="lms-catalog-course" data-course-id={course.id}>
      <Link to={catalogPaths.list()} className={`inline-flex items-center gap-1 text-sm ${linkCls}`}>
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Catalog
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100" data-testid="lms-course-title">
            {course.title}
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {`${CATEGORY_LABELS[course.category]} \u00b7 ${KIND_LABELS[course.kind]} \u00b7 ${course.current_version ? `current version ${course.current_version.version_no}` : 'not published yet'}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {draft ? (
            <Link to={catalogPaths.version(draft.id)} className={primaryBtnCls} data-testid="lms-open-draft">
              <Pencil className="h-4 w-4" aria-hidden="true" />
              {`Open draft v${draft.version_no}`}
            </Link>
          ) : (
            <>
              {course.current_version ? (
                <button type="button" className={primaryBtnCls} onClick={() => newDraft('current')} disabled={creating} data-testid="lms-new-draft-current">
                  <FilePlus2 className="h-4 w-4" aria-hidden="true" />
                  New draft from the current version
                </button>
              ) : null}
              <button type="button" className={secondaryBtnCls} onClick={() => newDraft('empty')} disabled={creating} data-testid="lms-new-draft-empty">
                <FilePlus2 className="h-4 w-4" aria-hidden="true" />
                New empty draft
              </button>
            </>
          )}
          {builder.available ? (
            course.current_version && !draft ? (
              <Link to={builderPaths.build(course.id)} className={secondaryBtnCls} data-testid="lms-builder-add-content">
                <FileUp className="h-4 w-4" aria-hidden="true" />
                Add content
              </Link>
            ) : (
              <span
                title={draft ? 'Publish or discard the open draft first: the builder writes its own new draft.' : 'Publish a first version before adding content with the builder.'}
                data-testid="lms-builder-add-content-blocked"
              >
                <button type="button" className={secondaryBtnCls} disabled aria-describedby="lms-builder-add-content-why">
                  <FileUp className="h-4 w-4" aria-hidden="true" />
                  Add content
                </button>
                <span id="lms-builder-add-content-why" className="sr-only">
                  {draft ? 'Publish or discard the open draft first.' : 'Publish a first version first.'}
                </span>
              </span>
            )
          ) : null}
          {editorOn && aiAllowed && (draft || course.current_version) ? (
            draft ? (
              <Link to={withPanel(draft.id)} className={secondaryBtnCls} data-testid="lms-edit-with-ai">
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                Edit with AI
              </Link>
            ) : (
              <button type="button" className={secondaryBtnCls} onClick={() => setAiConfirmOpen(true)} disabled={creating} data-testid="lms-edit-with-ai">
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                Edit with AI
              </button>
            )
          ) : null}
          {packs.length > 0 && !retired ? (
            <button type="button" className={secondaryBtnCls} onClick={() => setPackOpen(true)} data-testid="lms-image-pack-open">
              <ImageDown className="h-4 w-4" aria-hidden="true" />
              Import image pack
            </button>
          ) : null}
          {!retired ? (
            <button type="button" className={outlineBtnCls()} onClick={() => setRetireOpen(true)} data-testid="lms-retire-course">
              Retire course
            </button>
          ) : null}
        </div>
      </div>

      {packs.length > 0 ? (
        <ImagePackModal
          isOpen={packOpen}
          onClose={() => setPackOpen(false)}
          courseId={course.id}
          packs={packs}
          onApplied={packApplied}
          onOpenDraft={(versionId) => {
            setPackOpen(false);
            navigate(catalogPaths.version(versionId));
          }}
        />
      ) : null}

      {retired ? (
        <div className={noticeCls('amber')} role="status">
          This course is retired: nobody is assigned it and it stays in transcripts (8.1).
        </div>
      ) : null}

      <section className={`${cardCls} space-y-3`} aria-labelledby="lms-course-details-title" data-testid="lms-course-details">
        <h3 id="lms-course-details-title" className={subheadingCls}>
          Details
        </h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <TextInput label="Title" value={form.title} onChange={(title) => setForm({ ...form, title })} testId="lms-course-edit-title" />
          <TextInput
            label="Slug"
            value={form.slug}
            onChange={(slug) => setForm({ ...form, slug })}
            disabled={!slugEditable}
            hint={slugEditable ? 'Editable until the first publish (8.1).' : 'Fixed since the first publish (8.1).'}
          />
          <NumberInput
            label="Valid for (months)"
            value={form.validity_months}
            onChange={(validity_months) => setForm({ ...form, validity_months })}
            min={1}
            max={120}
            allowEmpty
            placeholder="No expiry"
            hint="Empty means a completion never expires."
          />
          <NumberInput
            label="Renewal assigned (days before expiry)"
            value={form.renewal_lead_days}
            onChange={(renewal_lead_days) => setForm({ ...form, renewal_lead_days })}
            min={0}
            max={365}
          />
        </div>
        <TextArea label="Description" value={form.description} onChange={(description) => setForm({ ...form, description })} rows={2} />
        <Checkbox
          label="Active (offered in the catalog and assignable)"
          checked={form.is_active}
          onChange={(is_active) => setForm({ ...form, is_active })}
        />
        <div className="flex justify-end">
          <button type="button" className={primaryBtnCls} onClick={saveDetails} disabled={!dirty || saving} data-testid="lms-course-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Save className="h-4 w-4" aria-hidden="true" />}
            Save details
          </button>
        </div>
      </section>

      <section className="space-y-2" aria-labelledby="lms-course-versions-title">
        <h3 id="lms-course-versions-title" className={subheadingCls}>
          {`Versions (${versions.length})`}
        </h3>
        {versions.length === 0 ? (
          <p className={hintCls}>This course has no versions yet.</p>
        ) : (
          <div className={tableWrapCls}>
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-testid="lms-course-versions">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th scope="col" className={thCls}>
                    Version
                  </th>
                  <th scope="col" className={thCls}>
                    Status
                  </th>
                  <th scope="col" className={thCls}>
                    Published
                  </th>
                  <th scope="col" className={thCls}>
                    Retrain policy
                  </th>
                  <th scope="col" className={thCls}>
                    Change summary
                  </th>
                  <th scope="col" className={thCls}>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {versions.map((v) => (
                  <tr key={v.id} className={rowCls}>
                    <td className={`${tdCls} whitespace-nowrap tabular-nums`}>{`Version ${v.version_no}`}</td>
                    <td className={tdCls}>
                      <span className={pillCls(STATUS_TONES[v.status])}>{STATUS_LABELS[v.status]}</span>
                    </td>
                    <td className={`${tdCls} whitespace-nowrap`}>{formatDate(v.published_at)}</td>
                    <td className={tdCls}>{v.retrain_policy ? POLICY_TEXT[v.retrain_policy].label : '\u2014'}</td>
                    <td className={`${tdCls} max-w-md`}>
                      <span className="line-clamp-2 text-gray-700 dark:text-gray-300">{v.change_summary || '\u2014'}</span>
                    </td>
                    <td className={`${tdCls} whitespace-nowrap text-right`}>
                      <div className="flex justify-end gap-2">
                        <Link to={catalogPaths.version(v.id)} className={outlineBtnCls()}>
                          {v.status === 'draft' ? <Pencil className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                          {v.status === 'draft' ? 'Edit' : 'View'}
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {versions.length >= 2 ? (
        <section className={`${cardCls} space-y-3`} aria-labelledby="lms-compare-title" data-testid="lms-compare">
          <h3 id="lms-compare-title" className={subheadingCls}>
            Compare two versions
          </h3>
          <div className="flex flex-wrap items-end gap-3">
            <SelectField className="w-64" label="What changed in" value={compareA} onChange={setCompareA} options={versionOptions} />
            <SelectField className="w-64" label="Since" value={compareB} onChange={setCompareB} options={versionOptions} />
            <button
              type="button"
              className={secondaryBtnCls}
              disabled={!compareA || !compareB || compareA === compareB}
              onClick={() => navigate(catalogPaths.diff(compareA, compareB))}
              data-testid="lms-compare-open"
            >
              <GitCompare className="h-4 w-4" aria-hidden="true" />
              Compare
            </button>
          </div>
        </section>
      ) : null}

      <ConfirmModal
        isOpen={aiConfirmOpen}
        onClose={() => {
          if (!creating) setAiConfirmOpen(false);
        }}
        onConfirm={() => void startAiDraft()}
        title="Edit with AI"
        message={`Start a draft from version ${course.current_version ? course.current_version.version_no : ''} to edit it with AI? Nothing is published until you publish it.`}
        confirmLabel="Start the draft"
        loading={creating}
      />

      <Modal isOpen={retireOpen} onClose={retiring ? () => {} : () => setRetireOpen(false)} title={`Retire ${course.title}?`} maxWidth="max-w-lg">
        <div className="space-y-3" data-testid="lms-retire-dialog">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            The current version is retired, every open assignment of this course is cancelled, and profiles stop requiring it. People who hold it keep their completions and certificates, and the course stays in transcripts (8.1).
          </p>
          <TextArea label="Reason (required)" value={retireReason} onChange={setRetireReason} rows={2} testId="lms-retire-reason" />
          <div className="flex justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
            <button type="button" className={secondaryBtnCls} onClick={() => setRetireOpen(false)} disabled={retiring}>
              Cancel
            </button>
            <button type="button" className={dangerBtnCls} onClick={retire} disabled={!retireReason.trim() || retiring} data-testid="lms-retire-confirm">
              {retiring ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              Retire course
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default CoursePage;
